'use client';

import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import {
  Bell,
  BookOpen,
  Check,
  FileText,
  Globe,
  Loader2,
  MessageCircle,
  MessageSquareWarning,
  Monitor,
  Send,
  Smile,
  Upload,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Switch } from '@kit/ui/switch';
import { Textarea } from '@kit/ui/textarea';
import { cn } from '@kit/ui/utils';

import darkSurfaceStyles from './website-dark-surface.module.css';
import { WebsiteGlassSurface } from './website-glass-surface';
import styles from './website-workflow-demos.module.css';

export type WebsiteWorkflowKind =
  | 'knowledge'
  | 'conversation'
  | 'action'
  | 'monitor'
  | 'team'
  | 'escalation';
export interface WebsiteWorkflowDemosProps {
  tone?: 'light' | 'dark';
  motion?: 'auto' | 'static';
  kinds?: readonly WebsiteWorkflowKind[];
}

// One deterministic clock per visible tile. Processing is illustrative, not a latency claim.
const BEATS = {
  focus: 800,
  inputEnd: 3300,
  action: 3800,
  processing: 4150,
  result: 5100,
  restart: 16500,
};
const KINDS: readonly WebsiteWorkflowKind[] = [
  'knowledge',
  'conversation',
  'action',
  'monitor',
  'team',
  'escalation',
];
const ICONS = {
  knowledge: BookOpen,
  conversation: MessageCircle,
  action: Zap,
  monitor: Monitor,
  team: MessageSquareWarning,
  escalation: Bell,
};

function useDemoClock(paused: boolean, replay: number, staticState: boolean) {
  const ref = useRef<HTMLElement>(null);
  const clockState = useRef({ elapsed: 0, cycle: 0, replay });
  const [cycle, setCycle] = useState(0);
  const [elapsed, setElapsed] = useState(BEATS.result);
  const [running, setRunning] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const element = ref.current;
    let visible = false;
    if (clockState.current.replay !== replay)
      clockState.current = { elapsed: 0, cycle: 0, replay };
    setCycle(clockState.current.cycle);
    let elapsedTime = clockState.current.elapsed;
    let previous = 0;
    let frame = 0;
    let lastPaint = -100;

    function stop() {
      cancelAnimationFrame(frame);
      frame = 0;
      previous = 0;
      setRunning(false);
    }
    function tick(now: number) {
      if (previous) elapsedTime += Math.min(now - previous, 120);
      previous = now;
      if (elapsedTime >= BEATS.restart) {
        elapsedTime = 0;
        clockState.current.cycle += 1;
        setCycle(clockState.current.cycle);
        setElapsed(0);
      }
      clockState.current.elapsed = elapsedTime;
      if (now - lastPaint >= 65) {
        setElapsed(elapsedTime);
        lastPaint = now;
      }
      frame = requestAnimationFrame(tick);
    }
    function update() {
      const reduce = media.matches || staticState;
      setReducedMotion(reduce);
      if (reduce) {
        stop();
        setElapsed(BEATS.result);
      } else if (visible && !document.hidden && !paused) {
        if (!frame) {
          setRunning(true);
          frame = requestAnimationFrame(tick);
        }
      } else stop();
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = Boolean(entry?.isIntersecting);
        update();
      },
      { threshold: 0.2 },
    );
    if (element) observer.observe(element);
    document.addEventListener('visibilitychange', update);
    media.addEventListener('change', update);
    if (!media.matches && !staticState) setElapsed(elapsedTime);
    update();
    return () => {
      stop();
      observer.disconnect();
      document.removeEventListener('visibilitychange', update);
      media.removeEventListener('change', update);
    };
  }, [paused, replay, staticState]);

  return { ref, elapsed, cycle, running, reducedMotion };
}

function typedText(text: string, elapsed: number) {
  // Small deterministic punctuation pauses, with every character in normal text flow.
  const available = Math.max(0, elapsed - BEATS.focus);
  let duration = 0;
  let count = 0;
  const cadence = Math.max(
    24,
    (BEATS.inputEnd - BEATS.focus - 160) / text.length,
  );
  for (const character of text) {
    duration += cadence + (/[.,?]/.test(character) ? 80 : 0);
    if (duration > available) break;
    count++;
  }
  return elapsed >= BEATS.inputEnd ? text : text.slice(0, count);
}

function WorkflowTile({
  tone,
  kind,
  paused,
  replay,
  motion,
}: {
  tone: 'light' | 'dark';
  kind: WebsiteWorkflowKind;
  paused: boolean;
  replay: number;
  motion: 'auto' | 'static';
}) {
  const { t } = useTranslation('marketing');
  const { ref, elapsed, cycle, running, reducedMotion } = useDemoClock(
    paused,
    replay,
    motion === 'static',
  );
  const done = elapsed >= BEATS.result;
  const processing = elapsed >= BEATS.processing && !done;
  const pressed = elapsed >= BEATS.action && elapsed < BEATS.processing;
  const focused = elapsed >= BEATS.focus && elapsed < BEATS.action;
  const sent = elapsed >= BEATS.processing;
  const Icon = ICONS[kind];
  const key = (name: string) => t(`home.workflowDemo.${name}`);
  const knowledgeMode = reducedMotion
    ? 'overview'
    : (['website', 'qa', 'upload'] as const)[cycle % 3];
  const monitorAttention = reducedMotion || done;
  const inputKey =
    kind === 'knowledge' && knowledgeMode === 'website'
      ? 'knowledge.website.input'
      : `${kind}.input`;
  const input = typedText(key(inputKey), elapsed);
  const phase = done
    ? 'result'
    : processing
      ? 'processing'
      : pressed
        ? 'action'
        : focused
          ? 'input'
          : 'initial';

  const sendButton = (
    <Button
      asChild
      size="icon"
      className={cn('h-8 w-8 shrink-0 rounded-full', pressed && styles.pressed)}
    >
      <span>
        {processing ? (
          <Loader2 className={cn('h-3.5 w-3.5', styles.spinner)} />
        ) : (
          <Send className="h-3.5 w-3.5" />
        )}
      </span>
    </Button>
  );

  return (
    <article
      ref={ref}
      data-workflow-demo={kind}
      data-tone={tone}
      data-scene={
        kind === 'knowledge'
          ? knowledgeMode
          : kind === 'monitor'
            ? monitorAttention
              ? 'attention'
              : 'live'
            : kind
      }
      data-phase={phase}
      data-running={running}
      data-reduced-motion={reducedMotion}
      className={styles.tile}
    >
      <WebsiteGlassSurface
        tone={tone}
        className={cn(
          styles.card,
          'flex h-full min-w-0 flex-col gap-5 rounded-3xl p-5 sm:p-6',
        )}
      >
        <div className={styles.cardHeading}>
          <span className={styles.number} aria-hidden="true">
            {String(KINDS.indexOf(kind) + 1).padStart(2, '0')}
          </span>
          <h3 className="text-lg font-medium tracking-tight">
            {key(`${kind}.title`)}
          </h3>
          <p className="text-muted-foreground max-w-md text-sm leading-relaxed">
            {key(`${kind}.description`)}
          </p>
        </div>
        <div className={styles.stage} aria-hidden="true">
          <div className={styles.bar}>
            <Icon className="h-4 w-4 shrink-0" />
            <span className="min-w-0 truncate">{key(`${kind}.surface`)}</span>
          </div>
          <div className={styles.body}>
            {kind === 'knowledge' && (
              <>
                <div className={styles.tabs}>
                  {(['website', 'qa', 'upload'] as const).map((mode) => (
                    <span
                      key={mode}
                      data-active={
                        knowledgeMode === mode ||
                        (knowledgeMode === 'overview' && mode === 'website')
                      }
                    >
                      {key(`knowledge.${mode}Tab`)}
                    </span>
                  ))}
                </div>
                {knowledgeMode === 'overview' ? (
                  <>
                    <p className={styles.label}>
                      {key('knowledge.sourcesLabel')}
                    </p>
                    <div className={styles.sourceRow}>
                      <Globe className="h-4 w-4 shrink-0" />
                      <span>{key('knowledge.website.input')}</span>
                      <Check className="ml-auto h-3.5 w-3.5" />
                    </div>
                    <div className={styles.sourceRow}>
                      <BookOpen className="h-4 w-4 shrink-0" />
                      <span>{key('knowledge.qaSource')}</span>
                      <Check className="ml-auto h-3.5 w-3.5" />
                    </div>
                    <div className={styles.sourceRow}>
                      <FileText className="h-4 w-4 shrink-0" />
                      <span>{key('knowledge.file')}</span>
                      <Check className="ml-auto h-3.5 w-3.5" />
                    </div>
                    <p className={styles.outcome}>
                      {key('knowledge.trainingBoundary')}
                    </p>
                  </>
                ) : knowledgeMode === 'website' ? (
                  <>
                    <p className={styles.label}>
                      {key('knowledge.website.label')}
                    </p>
                    <Input
                      readOnly
                      tabIndex={-1}
                      value={input}
                      placeholder="example.com"
                      className={cn('h-10 text-xs', focused && styles.focused)}
                    />
                    <p className={styles.label}>
                      {key('knowledge.website.crawl')}
                    </p>
                    <Button
                      asChild
                      size="sm"
                      className={cn(
                        'mr-auto h-8 text-xs',
                        pressed && styles.pressed,
                      )}
                    >
                      <span>
                        {processing && (
                          <Loader2
                            className={cn('mr-1.5 h-3.5 w-3.5', styles.spinner)}
                          />
                        )}
                        {key(
                          processing
                            ? 'knowledge.website.loading'
                            : 'knowledge.website.add',
                        )}
                      </span>
                    </Button>
                    <div className={styles.outcome} data-visible={done}>
                      <Check className="h-3.5 w-3.5 shrink-0" />
                      {key('knowledge.website.result')}
                    </div>
                  </>
                ) : knowledgeMode === 'upload' ? (
                  <>
                    <div
                      className={cn(styles.upload, focused && styles.focused)}
                    >
                      <Upload className="h-5 w-5" />
                      <p>
                        {elapsed >= BEATS.focus
                          ? key('knowledge.file')
                          : key('knowledge.uploadPrompt')}
                      </p>
                      <p className={styles.label}>
                        {key('knowledge.uploadTypes')}
                      </p>
                    </div>
                    <Button
                      asChild
                      size="sm"
                      className={cn(
                        'mr-auto h-8 text-xs',
                        pressed && styles.pressed,
                      )}
                    >
                      <span>
                        {processing && (
                          <Loader2
                            className={cn('mr-1.5 h-3.5 w-3.5', styles.spinner)}
                          />
                        )}
                        {key(
                          processing
                            ? 'knowledge.uploading'
                            : 'knowledge.uploadAction',
                        )}
                      </span>
                    </Button>
                    <div className={styles.outcome} data-visible={done}>
                      <Check className="h-3.5 w-3.5 shrink-0" />
                      {key('knowledge.uploadResult')}
                    </div>
                  </>
                ) : (
                  <>
                    <Input
                      readOnly
                      tabIndex={-1}
                      value={key('knowledge.question')}
                      className="h-9 text-xs"
                    />
                    <Textarea
                      readOnly
                      tabIndex={-1}
                      value={input}
                      placeholder={key('knowledge.answerLabel')}
                      rows={2}
                      className={cn(styles.input, focused && styles.focused)}
                    />
                    <Button
                      asChild
                      size="sm"
                      className={cn(
                        'mr-auto h-8 text-xs',
                        pressed && styles.pressed,
                      )}
                    >
                      <span>
                        {processing && (
                          <Loader2
                            className={cn('mr-1.5 h-3.5 w-3.5', styles.spinner)}
                          />
                        )}
                        {key(
                          processing ? 'knowledge.saving' : 'knowledge.save',
                        )}
                      </span>
                    </Button>
                    <div className={styles.outcome} data-visible={done}>
                      <Check className="h-3.5 w-3.5 shrink-0" />
                      {key('knowledge.result')}
                    </div>
                  </>
                )}
              </>
            )}
            {kind === 'conversation' && (
              <>
                <div className="flex items-center gap-2.5">
                  <Image
                    src="/images/agents/aiman-avatar.jpg"
                    alt=""
                    width={512}
                    height={512}
                    className="h-8 w-8 rounded-full"
                  />
                  <div>
                    <p className="text-xs font-medium">Aiman</p>
                    <p className={styles.label}>{key('conversation.role')}</p>
                  </div>
                </div>
                <div className={styles.chatMessages}>
                  <p className={styles.customerBubble} data-visible={sent}>
                    {key('conversation.input')}
                  </p>
                  {processing && (
                    <div className={styles.thinking}>
                      <span />
                      <span />
                      <span />
                    </div>
                  )}
                  <p className={styles.agentBubble} data-visible={done}>
                    {key('conversation.result')}
                  </p>
                </div>
                <div className={cn(styles.composer, focused && styles.focused)}>
                  <p className="min-w-0 flex-1 text-xs leading-relaxed">
                    {sent || !input ? (
                      <span className="text-muted-foreground">
                        {key('messagePlaceholder')}
                      </span>
                    ) : (
                      input
                    )}
                  </p>
                  {sendButton}
                </div>
              </>
            )}
            {kind === 'action' && (
              <>
                <p className={styles.label}>{key('action.context')}</p>
                <div
                  className={cn(
                    'border-border flex items-center justify-between gap-4 rounded-xl border p-4',
                    focused && styles.focused,
                  )}
                >
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-medium">{key('action.name')}</p>
                    <p className={styles.label}>{key('action.connection')}</p>
                  </div>
                  <Switch
                    tabIndex={-1}
                    disabled
                    checked={pressed || processing || done}
                    className="shrink-0 disabled:opacity-100"
                  />
                </div>
                <div className="border-border space-y-2 rounded-xl border border-dashed p-4">
                  <p className={styles.label}>
                    {key('action.instructionsLabel')}
                  </p>
                  <p className="text-sm leading-relaxed">
                    {key('action.input')}
                  </p>
                </div>
                <div className={styles.outcome} data-visible={done}>
                  <Check className="h-3.5 w-3.5 shrink-0" />
                  {key('action.result')}
                </div>
              </>
            )}
            {kind === 'monitor' && (
              <div className={styles.monitorWorkspace}>
                <aside className={styles.monitorSidebar}>
                  <div className={styles.monitorGroup}>
                    NEEDS ATTENTION <b>{monitorAttention ? 1 : 0}</b>
                  </div>
                  <div className={styles.monitorSlot} />
                  <div className={styles.monitorGroup}>
                    LIVE <b>{monitorAttention ? 0 : 1}</b>
                  </div>
                  <div className={styles.monitorSlot} />
                  <div className={styles.monitorGroup}>
                    IDLE <b>0</b>
                  </div>
                  <div
                    className={styles.monitorVisitor}
                    data-attention={monitorAttention}
                  >
                    <strong>Alex Morgan</strong>
                    <span>Group booking enquiry</span>
                    <small>
                      {monitorAttention
                        ? 'Needs your team'
                        : 'Sarah · AI agent'}
                    </small>
                    <span className={styles.monitorSentiment}>
                      <Smile size={13} />
                      <span>Positive sentiment</span>
                    </span>
                  </div>
                </aside>
                <section className={styles.monitorConversation}>
                  <div className={styles.monitorConversationTitle}>
                    <span>Alex Morgan</span>
                    <strong>Chat</strong>
                  </div>
                  <p className={styles.monitorCustomer}>
                    Could someone help with our group booking?
                  </p>
                  <div className={styles.monitorAgent}>
                    <div>
                      <strong>Sarah</strong> <span>AI agent</span>
                    </div>
                    {elapsed >= 3300 && !monitorAttention ? (
                      <div className={styles.monitorThinking}>
                        <i />
                        <i />
                        <i />
                      </div>
                    ) : (
                      <p>
                        {monitorAttention
                          ? 'I’ll bring your request to the team so they can help with the details.'
                          : 'I can help you get started with your group booking.'}
                      </p>
                    )}
                  </div>
                  <div
                    className={styles.monitorAttention}
                    data-visible={monitorAttention}
                  >
                    <Bell size={18} />
                    <div>
                      <strong>Needs attention</strong>
                      <span>Group booking enquiry</span>
                    </div>
                  </div>
                  <div className={styles.monitorTools}>
                    <span data-active={monitorAttention}>Take Over</span>
                    <span>Instruct Agent</span>
                    <span>Private Note</span>
                  </div>
                </section>
              </div>
            )}
            {kind === 'escalation' && (
              <>
                <div className={styles.notificationHeading}>
                  <span className={styles.notificationIcon}>
                    <Bell className="h-5 w-5" />
                    {done && (
                      <span className={styles.notificationCount}>1</span>
                    )}
                  </span>
                  <div>
                    <p className="text-sm font-medium">
                      {key(done ? 'escalation.notified' : 'escalation.group')}
                    </p>
                    <p className={styles.label}>
                      {key('escalation.groupName')}
                    </p>
                  </div>
                </div>
                <div className={styles.notificationMessage} data-visible={done}>
                  <p className="text-sm leading-relaxed font-medium">
                    {key('escalation.result')}
                  </p>
                  <p className={styles.label}>{key('escalation.reason')}</p>
                </div>
                <div className={styles.sourceRow}>
                  <span>{key('escalation.channel')}</span>
                  <span className="ml-auto text-xs">
                    {key('escalation.inApp')}
                  </span>
                </div>
                <p className={styles.outcome}>{key('escalation.boundary')}</p>
              </>
            )}
            {kind === 'team' && (
              <>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  {key('team.context')}
                </p>
                <div className={styles.instruction}>
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-medium">
                    <MessageSquareWarning className="h-3.5 w-3.5" />
                    {key(done ? 'team.result' : 'team.surface')}
                  </p>
                  <p className="min-h-12 text-sm leading-relaxed">
                    {input || (
                      <span className="opacity-70">
                        {key('team.placeholder')}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className={styles.instructionPill}>
                    {key('team.surface')}
                  </span>
                  {sendButton}
                </div>
                <div className={styles.outcome} data-visible={done}>
                  <Check className="h-3.5 w-3.5 shrink-0" />
                  {key('team.private')}
                </div>
              </>
            )}
          </div>
        </div>
        <p className="sr-only">
          {kind === 'monitor'
            ? 'Sarah brings Alex’s group booking enquiry from Live to Needs Attention, with its conversation and team controls in view.'
            : key(`${kind}.summary`)}
        </p>
      </WebsiteGlassSurface>
    </article>
  );
}

/** Isolated product presentations: only Pause / Resume / Replay are interactive. */
export function WebsiteWorkflowDemos({
  tone = 'light',
  motion = 'auto',
  kinds = KINDS,
}: WebsiteWorkflowDemosProps) {
  const { t } = useTranslation('marketing');
  const paused = false;
  const replay = 0;
  return (
    <div
      data-workflow-tone={tone}
      className={cn(
        styles.root,
        'space-y-5',
        tone === 'dark' && [
          'dark rounded-3xl p-6 sm:p-9',
          darkSurfaceStyles.surface,
        ],
      )}
    >
      <p className="sr-only">{t('home.workflowDemo.accessibleDescription')}</p>
      <div className={styles.grid} data-count={kinds.length}>
        {kinds.map((kind) => (
          <WorkflowTile
            key={kind}
            tone={tone}
            kind={kind}
            paused={paused}
            replay={replay}
            motion={motion}
          />
        ))}
      </div>
    </div>
  );
}
